import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { setReplyMessage } from '@/store/chat/actions';

import { selectChat } from '@/store/chat/selectors';
import { selectUsers } from '@/store/users/selectors';

import { Avatar } from 'antd';
import MarkdownViewer from '@/components/MarkdownViewer';
import { FiCopy, FiCornerUpLeft } from 'react-icons/fi';

import File from './File';
import Photo from './Photo';

import { useLocalFileByUUID } from '@shared/hooks/localFiles';
import useIsMobile from '@shared/hooks/useIsMobile';

import classNames from 'classnames';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import styles from '../Messages.module.scss';

import AvatarPlaceholder from '@/assets/images/PortraitPlaceholder.png';

let pressTimer;

const getLogoByBotName = botName => {
  switch (botName) {
    case 'Помощник-методист':
      return '/assets/img/icons/female-teach.svg';
    case 'Консультант по развитию':
      return '/assets/img/icons/rocket.svg';
    case 'Changelog Bot':
      return '/assets/img/icons/bell.svg';
    default:
      return AvatarPlaceholder;
  }
};
const Message = ({ data, containerRef }) => {
  const { selectedUser, messages } = useSelector(selectChat);
  const { currentUser } = useSelector(selectUsers);
  const { t } = useTranslation('chat');

  const [visiblePopover, setVisiblePopover] = useState(false);
  const [upMessage, setUpMessage] = useState(false);

  const popoverRef = useRef(null);
  const messageRef = useRef(null);
  const messageBoxRef = useRef(null);
  const dispatch = useDispatch();
  const [isTablet] = useIsMobile(1201);
  const isNotCurrentUser = useMemo(() => currentUser?.id !== data?.userData?.id, []);

  useEffect(() => {
    if (isTablet) {
      messageRef.current?.addEventListener('touchstart', handleMouseDown);
      messageRef.current?.addEventListener('touchend', handleMouseUp);
    } else {
      messageRef.current?.addEventListener('contextmenu', handleRightClick);
    }
    document.addEventListener('click', handleLeftClick);

    return () => {
      if (isTablet) {
        messageRef.current?.removeEventListener('touchstart', handleMouseDown);
        messageRef.current?.removeEventListener('touchend', handleMouseUp);
      } else {
        messageRef.current?.removeEventListener('contextmenu', handleRightClick);
      }
      document.removeEventListener('click', handleLeftClick);
    };
  }, [messageRef, popoverRef, isTablet]);

  const handleMouseDown = event => {
    pressTimer = setTimeout(() => {
      setVisiblePopover(true);
      const messagesList = containerRef.current.getBoundingClientRect();
      const touchY = event.touches[0].screenY;
      const isLongMessages = messageBoxRef.current.offsetHeight > 200;
      const scrollPosition = popoverRef.current;
      if (touchY - messagesList.top > messagesList.height - 120 || isLongMessages) {
        setUpMessage(true);
      }

      setTimeout(() => {
        scrollPosition.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 300);
      document.body.style.overflow = 'hidden';
    }, 500);
  };

  const handleMouseUp = () => {
    clearTimeout(pressTimer);
  };

  const handleRightClick = event => {
    event.preventDefault();

    if (data.messageData.isSystem) return;

    // Закрываем все открытые popover
    messageBoxRef?.current?.click();

    const messageBox = messageBoxRef.current;
    const popover = popoverRef.current;

    //размеры и позиции элементов
    const messageRect = messageBox.getBoundingClientRect();
    const viewportHeight = containerRef.current.clientHeight;
    const viewportWidth = containerRef.current.clientWidth;
    const popoverHeight = 54;
    const popoverWidth = currentUser.language === 'en_US' ? 94 : 143;
    // Расчет позиции по вертикали
    let topPosition;

    if (messageRect.top < -200) {
      // Если сообщение выше видимой области чата
      topPosition = event.pageY - 100;
    } else if (messageRect.top < viewportHeight / 2) {
      // Если сообщение в верхней половине экрана
      topPosition = event.pageY - 120;
    } else {
      // Если сообщение в нижней половине экрана
      topPosition = event.pageY - 180;
    }
    // Проверка на выход за границы экрана
    if (topPosition < 0) {
      topPosition = 110;
    }
    //Проверка на выход левой нижней части за границы экрана
    if (topPosition + popoverHeight > viewportHeight) {
      topPosition = viewportHeight - 10;
    }
    // Расчет позиции по горизонтали
    let leftPosition;

    if (isNotCurrentUser) {
      // Если сообщение в левой половине экрана - показываем справа от него
      leftPosition =
        messageBoxRef.current.offsetLeft + messageBoxRef.current.offsetWidth + messageBoxRef.current.clientLeft + 10;
    } else {
      // Если сообщение в правой половине экрана - показываем слева от него
      leftPosition = messageBoxRef.current.offsetLeft - popoverWidth;
    }

    if (leftPosition < 0) leftPosition = messageBoxRef.current.offsetWidth + 28;
    if (leftPosition + popoverWidth > viewportWidth) {
      leftPosition = viewportWidth - popoverWidth - messageBoxRef.current.offsetWidth;
    }
    // Координаты позиционирования поповера
    popover.style.top = `${topPosition}px`;
    popover.style.left = `${leftPosition}px`;

    if (event?.button === 2) setVisiblePopover(true);
  };

  const handleLeftClick = () => {
    setVisiblePopover(false);
    setUpMessage(false);
    document.body.style.overflow = 'auto';
  };

  const replyMessage = useMemo(() => {
    if (data.messageData.forwardedMessageId) {
      return messages.find(message => message.messageData.messageId === data.messageData.forwardedMessageId);
    } else {
      return null;
    }
  }, []);

  const handleReply = () => {
    dispatch(
      setReplyMessage({
        content: data.messageData.content,
        messageId: data.messageData.messageId,
        userId: data.userData.id,
        userFirstName: data.userData.firstName,
        userLastName: data.userData.lastName,
        files: data.messageData.files,
      })
    );
    setVisiblePopover(false);
  };

  const handleCopyText = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(data.messageData.content ?? '');
    }
  };
  const contentPopover = (
    <>
      {!selectedUser?.bot?.botId && (
        <div className={styles.message__popoverContent} onClick={handleReply}>
          <FiCornerUpLeft />
          <p>{t('reply')}</p>
        </div>
      )}
      <div className={styles.message__popoverContent} onClick={handleCopyText}>
        <FiCopy />
        <p>{t('copy')}</p>
      </div>
    </>
  );

  const renderFiles = () => {
    if (data.messageData.files.length) {
      return data.messageData.files.map((elem, index) => {
        if (elem.fileType.includes('image')) {
          return <Photo fileData={elem} key={index} />;
        } else {
          return <File fileName={elem.fileName} fileUuid={elem.fileUuid} key={index} />;
        }
      });
    } else {
      return null;
    }
  };

  const renderFileText = () => {
    if (replyMessage?.files?.length >= 2) {
      return t('files.0');
    } else if (replyMessage?.files?.length === 1) {
      if (replyMessage?.files[0].fileType.includes('image')) {
        return t('files.2');
      } else {
        return t('files.1');
      }
    }
  };

  const messageContent =
    data.messageData.content === 'BREAKOUT_REPORT' ? t(data.messageData.content) : data.messageData.content;

  const [currentAvatar] = useLocalFileByUUID(data?.userData?.localPhotoUuid, { defaultImg: AvatarPlaceholder });

  return (
    <div
      className={classNames(styles.message, {
        [styles.message_currentUser]: currentUser?.id === data?.userData?.id,
        [styles.message_error]: data?.messageData?.isError,
      })}
    >
      <div ref={messageBoxRef} className={styles.message__box}>
        {isNotCurrentUser && (
          <Avatar
            shape={data?.messageData?.isBot ? 'square' : 'circle'}
            src={data?.messageData?.isBot ? getLogoByBotName(data?.userData?.firstName) : currentAvatar}
          />
        )}
        <div
          className={classNames(styles.message__content, {
            [styles.message__content_currentUser]: !isNotCurrentUser,
            [styles.message__content_active]: visiblePopover && isTablet,
            [styles.message__content_up]: upMessage,
            [styles.message__content_isSystem]: data.messageData.isSystem,
          })}
          ref={messageRef}
        >
          {replyMessage && (
            <div
              className={classNames(styles.message__reply, { [styles.message__reply_currentUser]: !isNotCurrentUser })}
            >
              <div className={styles.message__name}>
                {replyMessage?.userData?.userId === currentUser.id ? (
                  <>
                    {currentUser.firstName} {currentUser.lastName}
                  </>
                ) : (
                  <>
                    {replyMessage.userData?.firstName} {replyMessage.userData?.lastName}
                  </>
                )}
              </div>
              <p className={styles.message__replyContent} data-qa='messageText'>
                {replyMessage?.messageData?.content ? replyMessage.messageData.content : renderFileText()}
              </p>
            </div>
          )}
          {selectedUser?.authorId && isNotCurrentUser && (
            <p className={styles.message__name}>
              {data.userData?.firstName} {data.userData?.lastName}
            </p>
          )}
          {!!data.messageData.files?.length && <div className={styles.message__files}>{renderFiles()}</div>}
          <div className={styles.message__container}>
            <MarkdownViewer dataQa='messageContent'>{messageContent}</MarkdownViewer>
            {/* {!data.messageData.isSystem && ( */}
            <p className={styles.message__time}>{dayjs(data.messageData.createdAt).format('HH:mm')}</p>
            {/* )} */}
          </div>
        </div>
        <div
          ref={popoverRef}
          className={classNames(styles.popover, {
            [styles.popover_visible]: visiblePopover,
            [styles.popover_visibleNoUser]: isNotCurrentUser,
            [styles.popover_up]: upMessage,
          })}
        >
          {contentPopover}
        </div>
      </div>
      {isTablet && <div className={classNames(styles.overlay, { [styles.overlay_visible]: visiblePopover })} />}
    </div>
  );
};

Message.propTypes = {
  data: PropTypes.shape({
    messageData: PropTypes.shape({
      content: PropTypes.string,
      createdAt: PropTypes.string,
      messageId: PropTypes.number,
      userId: PropTypes.number,
      forwardedMessageId: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
      files: PropTypes.arrayOf(
        PropTypes.shape({
          fileName: PropTypes.string,
          fileUuid: PropTypes.string,
          fileType: PropTypes.string,
        })
      ),
    }),
    userData: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      localPhotoUuid: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
  }),
};

export default memo(Message);
